import { FilePlus02 } from "@untitledui/icons";
import { useState } from "react";
import { UNSAFE_PortalProvider } from "react-aria";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CohortLibraryEditor } from "./cohort-library-editor";
import { useCohortLibraryEditor } from "./use-cohort-library-editor";

export const CohortLibraryModal = ({
  open,
  onOpenChange,
  startInNewCohort = false,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startInNewCohort?: boolean;
  onSaved?: () => void;
}) => {
  const editor = useCohortLibraryEditor({ active: open, startWithNewCohort: startInNewCohort });
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);

  const handleSave = async () => {
    const ok = await editor.handleSave();
    if (ok) {
      onSaved?.();
      onOpenChange(false);
    }
  };

  // Intercept close attempts to guard unsaved changes.
  const handleOpenChange = (next: boolean) => {
    if (!next && editor.isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onOpenChange(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent ref={setContentNode} className="flex max-h-[85vh] flex-col sm:max-w-[72rem]">
          <DialogHeader>
            <DialogTitle data-testid="cohort-library-title">Manage Cohorts</DialogTitle>
            <DialogDescription>
              Organize your creators with custom cohorts to segment, filter, and reach the right people faster.
            </DialogDescription>
          </DialogHeader>

          <UNSAFE_PortalProvider getContainer={() => contentNode}>
            <CohortLibraryEditor editor={editor} />
          </UNSAFE_PortalProvider>

          <DialogFooter>
            <Button color="primary" iconLeading={FilePlus02} isLoading={editor.isPending} onClick={handleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved-changes guard */}
      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard changes?</DialogTitle>
            <DialogDescription>You have unsaved changes. Closing now will discard them.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </Button>
            <Button
              color="primary-destructive"
              size="sm"
              onClick={() => {
                setConfirmDiscard(false);
                onOpenChange(false);
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
